/**
 * orphan-option-cart-guard.js
 *
 * Backstop against "orphan option" carts.
 *
 * Hidden add-on products (product type `mw_product_option`, for example the
 * $0.00 "SIZE" product used by men's bands) exist only to be attached to a real
 * product. A cart that holds nothing but these is not a real order -- it is the
 * $0.00 abandoned checkout we are fixing.
 *
 * The primary fix lives in addon-pdp-form-controller.js, which refuses to submit
 * such a cart in the first place. This file catches everything the PDP cannot
 * see, such as a cart permalink (/cart/<variant_id>:1) or a stale link in an old
 * email, by removing the orphaned lines on page load or when the tab regains
 * focus.
 *
 * Deliberately conservative:
 *   - Only acts when EVERY line in the cart is an option carrier. A genuine cart
 *     (ring + size + engraving) is never touched.
 *   - Removes only the offending lines rather than clearing the whole cart.
 *   - Fails silently. If anything goes wrong the page behaves as normal.
 */

const OPTION_CARRIER_PRODUCT_TYPES = ['mw_product_option'];

let checking = false;

const routes = (window.Theme && window.Theme.routes) || {};
const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const cartJsonUrl = `${shopRoot}cart.js`;
const cartUpdateUrl = routes.cart_update_url ? `${routes.cart_update_url}.js` : `${shopRoot}cart/update.js`;
const cartPageUrl = routes.cart_url || `${shopRoot}cart`;

const isOptionCarrier = (item) => OPTION_CARRIER_PRODUCT_TYPES.indexOf(item.product_type) !== -1;

/**
 * A cart is orphaned only when BOTH are true:
 *   1. every line is a hidden option carrier, and
 *   2. the cart is worth nothing.
 *
 * The price test matters. A bundle-wrapper product can legitimately produce a
 * cart made up entirely of option carriers -- for example SIZE (free) plus
 * Inside Engraving ($150) -- and that is a real order we must not touch. Only a
 * cart that is both option-only and $0.00 is meaningless.
 */
const isOrphanedCart = (cart) => {
  if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return false;
  if (Number(cart.total_price) > 0) return false;
  return cart.items.every(isOptionCarrier);
};

const fetchCart = () =>
  fetch(cartJsonUrl, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }).then((response) => (response.ok ? response.json() : null));

const removeLines = (items) => {
  const updates = {};
  items.forEach((item) => {
    updates[item.key || item.variant_id] = 0;
  });

  return fetch(cartUpdateUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ updates }),
  });
};

const announce = (removedItems) => {
  try {
    document.dispatchEvent(
      new CustomEvent('cart:orphan-options-removed', {
        bubbles: true,
        detail: { items: removedItems },
      })
    );
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { sourceId: 'orphan-option-cart-guard', data: { source: 'orphan-option-cart-guard' } },
      })
    );
  } catch {
    /* no-op */
  }
};

const onCartPage = () => {
  const path = window.location.pathname.replace(/\/$/, '');
  return path === cartPageUrl.replace(/\/$/, '');
};

const guard = () => {
  if (checking) return Promise.resolve();
  checking = true;

  return fetchCart()
    .then((cart) => {
      if (!isOrphanedCart(cart)) return null;

      return removeLines(cart.items).then(() => {
        announce(cart.items);
        if (onCartPage()) window.location.reload();
      });
    })
    .catch(() => {
      /* never break the page over this */
    })
    .then(() => {
      checking = false;
    });
};

/* Do not sweep in response to /cart/add. A valid parent + SIZE submission is a
   coordinated multi-line cart write, and observing it mid-update can make the
   free SIZE line look orphaned. Run on load and when the tab regains focus
   instead. The focus check matters
   because a cart permalink opened in another tab can build an orphan cart that
   this tab has not seen yet. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', guard, { once: true });
} else {
  guard();
}

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) guard();
});

/* Exposed for manual testing from the browser console. */
window.orphanOptionCartGuard = { check: guard, isOrphanedCart };
